/**
 * Booking time granularity.
 *
 * Booking start/end times are entered in 15-minute increments: pickers step in
 * 15-minute units and typed values snap to the nearest 15 minutes, so a booking
 * can never be created at, say, 10:07.
 */

export const BOOKING_TIME_STEP_MINUTES = 15;
export const BOOKING_TIME_STEP_SECONDS = BOOKING_TIME_STEP_MINUTES * 60;

const STEP_MS = BOOKING_TIME_STEP_SECONDS * 1000;
const CLOCK_RE = /^(\d{2}):(\d{2})(?::\d{2})?$/;
const DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Snap a `HH:MM` clock value to the nearest 15 minutes (clamped inside the day). */
export function snapBookingClock(value: string): string {
  const match = CLOCK_RE.exec(value);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return value;
  const totalMinutes =
    Math.round((hours * 60 + minutes) / BOOKING_TIME_STEP_MINUTES) *
    BOOKING_TIME_STEP_MINUTES;
  const clamped = Math.min(totalMinutes, 24 * 60 - BOOKING_TIME_STEP_MINUTES);
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
}

/**
 * Snap a `YYYY-MM-DDTHH:MM` datetime-local value to the nearest 15 minutes.
 * Wall-clock digits are carried through UTC so day, month and year boundaries
 * roll correctly without a browser-timezone shift.
 */
export function snapBookingDateTime(value: string): string {
  const match = DATE_TIME_RE.exec(value);
  if (!match) return value;
  const [, year, month, day, hours, minutes] = match;
  const parsed = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  // Reject normalized impossible dates (e.g. 2026-02-30 → 2026-03-02).
  if (parsed.toISOString().slice(0, 16) !== `${year}-${month}-${day}T${hours}:${minutes}`) {
    return value;
  }
  const snapped = new Date(Math.round(parsed.getTime() / STEP_MS) * STEP_MS);
  return snapped.toISOString().slice(0, 16);
}

/** Hour options offered by booking time pickers (`00`–`23`). */
export const BOOKING_HOUR_OPTIONS: readonly string[] = Array.from(
  { length: 24 },
  (_, index) => pad(index)
);

/** Minute options offered by booking time pickers — 15-minute increments only. */
export const BOOKING_MINUTE_OPTIONS: readonly string[] = Array.from(
  { length: 60 / BOOKING_TIME_STEP_MINUTES },
  (_, index) => pad(index * BOOKING_TIME_STEP_MINUTES)
);

/**
 * Split a `HH:MM` (or `HH:MM:SS`) clock value into picker parts.
 * Returns `null` when the value is not a real time of day.
 */
export function splitBookingTime(value: string): { hour: string; minute: string } | null {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const hour = match[1];
  const minute = match[2];
  if (hour === undefined || minute === undefined) return null;
  if (Number(hour) > 23 || Number(minute) > 59) return null;
  return { hour, minute };
}

/**
 * Join picker parts into a `HH:MM` clock value. A stray minute outside the
 * 15-minute grid (for example `17`) snaps to the nearest mark, so invalid
 * values cannot be composed from the pickers.
 */
export function composeBookingTime(hour: string, minute: string): string {
  const hours = /^\d{2}$/.test(hour) ? hour : '00';
  const minutes = /^\d{2}$/.test(minute) ? minute : '00';
  return snapBookingClock(`${hours}:${minutes}`);
}